/**
 * Branding Kit — data model helpers.
 *
 * The single source of truth for how a school's branding is stored on its
 * `schools/{id}` document and read back. Keep every surface (theme controller,
 * settings UI, PDFs, emails) reading through here so field names never drift.
 *
 * The colour model is three roles: colour1 (dark), colour2 (accent), colour3
 * (light). The page stays neutral — near-white in light mode, colour1 clamped
 * dark in dark mode. The brand shows on buttons (colour1 light / colour2 dark),
 * badges & accents (colour2), and outlines (colour2 in both modes). See
 * palette.js for the mapping.
 *
 * Stored fields (top-level on the school doc — reuses the existing colour fields):
 *   brandingEnabled : boolean  — master switch; when true the app white-labels
 *   primaryColour   : hex      — colour 1 · dark   (existing field)
 *   secondaryColour : hex      — colour 2 · accent (existing field)
 *   tertiaryColour  : hex      — colour 3 · light  (existing field)
 *   logoUrl         : string   — school badge/logo (Storage URL) — new
 *   logoStoragePath : string   — for replace/delete — new
 */

// Swan defaults, used as fallbacks and as the SchoolProfilePage form seed.
// dark ink · brand teal accent · near-white light.
export const DEFAULT_BRAND = {
  primaryColour: '#0f172a',
  secondaryColour: '#06b6d4',
  tertiaryColour: '#f8fafc',
};

/** Read the three brand colours off a school doc, mapped to engine seed names. */
export function getSchoolBrandColours(school) {
  return {
    dark: school?.primaryColour || DEFAULT_BRAND.primaryColour,
    accent: school?.secondaryColour || DEFAULT_BRAND.secondaryColour,
    light: school?.tertiaryColour || DEFAULT_BRAND.tertiaryColour,
  };
}

/** Has the school turned white-label branding on (and given us a colour)? */
export function isBrandingActive(school) {
  return !!school?.brandingEnabled && !!school?.primaryColour;
}

/** The school's badge/logo URL, or null. */
export function getSchoolLogoUrl(school) {
  return school?.logoUrl || null;
}
